import AppBar from "@material-ui/core/AppBar";
import Button from "@material-ui/core/Button";
import CloseIcon from "@material-ui/icons/Close";
import Dialog from "@material-ui/core/Dialog";
import IconButton from "@material-ui/core/IconButton";
import Slide from "@material-ui/core/Slide";
import Toolbar from "@material-ui/core/Toolbar";
import Typography from "@material-ui/core/Typography";
/* eslint-disable react/display-name */
import React, { FC, useCallback, useEffect, useState } from "react";
import { GridColDef } from "@material-ui/data-grid";
import { TransitionProps } from "@material-ui/core/transitions";
import { makeStyles } from "@material-ui/core/styles";

import BaseDataGrid from "../../../components/modules/BaseDataGrid";
import Loader from "../../../components/elements/Loader";
import Switch from "../../../components/elements/Switch";
import api from "../../../api";
import { CampaignType } from "../../../types/campaignTypes";
import {
  currencySortComparator,
  flatSortComparator,
  percentageSortComparator,
  transformRowValue,
} from "../../../helpers/campainRowFormatHelpers";
import { infoToaster, successToaster } from "../../../helpers/toasters";

const useStyles = makeStyles((theme) => ({
  appBar: {
    position: "relative",
  },
  title: {
    marginLeft: theme.spacing(2),
    flex: 1,
  },
  container: {
    height: "100vh",
  },
}));

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & { children?: React.ReactElement },
  ref: React.Ref<unknown>,
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

type SetStateAction<S> = S | ((prevState: S) => S);

type CampaignsDialogProps = {
  isOpen: boolean;
  campaigns: CampaignType[];
  advertiserName: string;
  handleClose: () => void;
  setWereCampaignsEdited: (wereCampaignsEdited: SetStateAction<boolean>) => void;
};

enum EditableFields {
  Active = "active",
  Archived = "archived",
}

type CampaignRow = {
  id: number;
  brandgenCampaignId: string;
  name: string;
  start_date: string;
  end_date: string;
  active: boolean;
  archived: boolean;
  matchedAccounts: number;
  clicks: number | string;
  clickThroughRate: number | string;
};

const updateCampagin = async (
  brandgenCampaignId: string,
  beeswaxCampaignId: number,
  body: Partial<{ archived: boolean; active: boolean }>,
) => {
  try {
    const { active, archived } = body;
    if (archived !== undefined) await api.archiveCampaign(brandgenCampaignId, { archived });
    if (active !== undefined) await api.updateCampaignStatus(brandgenCampaignId, beeswaxCampaignId, { active });
  } catch (error) {
    // eslint-disable-next-line no-console
    console.log("editCampagin error: ", error);
  }
};

const CampaignsDialog: FC<CampaignsDialogProps> = ({
  isOpen,
  campaigns,
  advertiserName,
  handleClose,
  setWereCampaignsEdited,
}) => {
  const muiClasses = useStyles();
  const [rows, setRows] = useState<CampaignRow[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const [editedRows, setEditedRows] = useState<CampaignRow[]>([]);

  const handleCampaignFieldChange = (editedRow: CampaignRow, field: EditableFields) => () => {
    setRows(rows.map((row) => (row.id === editedRow.id ? { ...row, [field]: !row[field] } : row)));

    const editedRowToSave = { ...editedRow, [field]: !editedRow[field] };
    const originalRow = campaigns.find((campaign) => campaign.beeswaxCampaignId === editedRow.id)!;

    const fieldsToCheckFor = Object.values(EditableFields);

    let areRowsDifferent = false;

    for (const editableField of fieldsToCheckFor) {
      if (editedRowToSave[editableField] !== originalRow[editableField]) {
        areRowsDifferent = true;
        break;
      }
    }

    if (areRowsDifferent) {
      const foundEditedRow = editedRows.find((row) => row.id === editedRow.id);

      if (foundEditedRow) {
        setEditedRows(editedRows.map((row) => (row.id === editedRow.id ? editedRowToSave : row)));
      } else {
        setEditedRows([...editedRows, editedRowToSave]);
      }
    } else {
      setEditedRows(editedRows.filter((row) => row.id !== editedRow.id));
    }
  };

  const handleSave = async () => {
    if (editedRows.length === 0) {
      infoToaster("There are no changes in rows to save.");
    } else {
      setIsLoading(true);
      await Promise.all(
        editedRows.map((changedRow) =>
          updateCampagin(changedRow.brandgenCampaignId, changedRow.id, {
            active: changedRow.active,
            archived: changedRow.archived,
          }),
        ),
      );
      setIsLoading(false);
      setWereCampaignsEdited(true);
      successToaster("Campaigns are successfully updated!", 4000);
      handleClose();
    }
  };

  const columns: GridColDef[] = [
    {
      field: "name",
      headerName: "Name",
      width: 450,
    },
    {
      field: "start_date",
      headerName: "Start Date",
      width: 150,
    },
    {
      field: "end_date",
      headerName: "End Date",
      width: 150,
    },
    {
      field: "active",
      headerName: "Active",
      width: 150,
      renderCell: (params: any) => (
        <Switch
          checked={params.row.active}
          onChange={handleCampaignFieldChange(params.row, EditableFields.Active)}
          disabled={isLoading || params.row.archived}
        />
      ),
    },
    {
      field: "archived",
      headerName: "Archived",
      width: 150,
      renderCell: (params: any) => (
        <Switch
          color="secondary"
          checked={params.row.archived}
          onChange={handleCampaignFieldChange(params.row, EditableFields.Archived)}
          disabled={isLoading || params.row.active}
        />
      ),
    },
    { field: "matchedAccounts", headerName: "Matched Accounts", width: 250 },
    { field: "clicks", headerName: "Clicks", width: 150, sortComparator: flatSortComparator },
    {
      field: "clickThroughRate",
      headerName: "CTR",
      width: 120,
      sortComparator: percentageSortComparator,
    },
    { field: "impressions", headerName: "Impressions", width: 250, sortComparator: flatSortComparator },
    { field: "budgetSpent", headerName: "Budget Spent", width: 250, sortComparator: currencySortComparator },
    { field: "costPerClick", headerName: "CPC (USD)", width: 150, sortComparator: currencySortComparator },
    { field: "costPerMile", headerName: "CPM (USD)", width: 150, sortComparator: currencySortComparator },
    { field: "conversions", headerName: "Conversions", width: 250, sortComparator: flatSortComparator },
  ];

  const setRowsAccordingly = useCallback(() => {
    const rowsToSet = campaigns.map((campaign) => {
      const { beeswaxCampaignId, brandgenCampaignId, name, start_date, end_date, active, archived, accounts } =
        campaign;

      const campaignReport = campaign?.campaignReports?.length ? campaign.campaignReports[0] : null;

      const { clicks, ctr, impressions, spend, cpm_usd, cpc_usd, conversions } = campaignReport ?? {};

      const campaignBasicInfo = {
        id: beeswaxCampaignId,
        brandgenCampaignId,
        name,
        start_date: new Date(start_date).toLocaleDateString(),
        end_date: new Date(end_date).toLocaleDateString(),
        active,
        archived,
        matchedAccounts: +accounts,
      };

      const campaignReportInfo = {
        clicks: transformRowValue(clicks, true, false, "FLAT"),
        clickThroughRate: transformRowValue(ctr, false, true, "PERCENTAGE"),
        impressions: transformRowValue(impressions, true, false, "FLAT"),
        budgetSpent: transformRowValue(spend, false, true, "CURRENCY"),
        costPerMile: transformRowValue(cpm_usd, false, true, "CURRENCY"),
        costPerClick: transformRowValue(cpc_usd, false, true, "CURRENCY"),
        conversions: transformRowValue(conversions, true, false, "FLAT"),
      };

      return {
        ...campaignBasicInfo,
        ...campaignReportInfo,
      };
    });
    setRows(rowsToSet);
  }, [campaigns]);

  useEffect(() => {
    setRowsAccordingly();
  }, [setRowsAccordingly]);

  useEffect(() => {
    if (!isOpen && editedRows.length !== 0) {
      setRowsAccordingly();
      setEditedRows([]);
    }
  }, [editedRows.length, isOpen, setRowsAccordingly]);

  return (
    <Dialog fullScreen open={isOpen} onClose={handleClose} TransitionComponent={Transition}>
      <AppBar className={muiClasses.appBar}>
        <Toolbar>
          <IconButton edge="start" color="inherit" onClick={handleClose} aria-label="close" disabled={isLoading}>
            <CloseIcon />
          </IconButton>
          <Typography variant="h6" className={muiClasses.title}>
            "{advertiserName}" Advertiser Campaigns
          </Typography>
          <Button color="inherit" onClick={handleSave} disabled={isLoading}>
            Save
          </Button>
        </Toolbar>
      </AppBar>

      <div className={muiClasses.container}>
        {isLoading && <Loader />}

        <BaseDataGrid columns={columns} rows={rows} />
      </div>
    </Dialog>
  );
};

export default CampaignsDialog;
